import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import './AdminWithdrawals.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AdminWithdrawals = () => {
  const { token } = useAuth();
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    fetchWithdrawals();
    const interval = setInterval(fetchWithdrawals, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchWithdrawals = async () => {
    try {
      const res = await fetch(`${API_BASE}/wallet/admin/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setWithdrawals(data.withdrawals);
      }
    } catch (error) {
      console.error('Fetch withdrawals error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsPaid = async (withdrawalId, userId, amount) => {
    setProcessingId(withdrawalId);
    try {
      const res = await fetch(`${API_BASE}/wallet/admin/mark-paid/${userId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        alert(`✅ Payment of $${amount.toLocaleString()} marked as sent.`);
        fetchWithdrawals();
        setShowConfirmModal(false);
      } else {
        alert(data.message || 'Failed to mark payment');
      }
    } catch (error) {
      alert('Error marking payment');
    } finally {
      setProcessingId(null);
    }
  };

  const openConfirmModal = (withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowConfirmModal(true);
  };

  const filteredWithdrawals = withdrawals.filter(w => {
    const matchesSearch = 
      (w.vendorId?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (w.vendorId?.email || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (w.accountDetails?.bankName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (w.accountDetails?.accountNumber || '').includes(searchTerm);
    const matchesStatus = filterStatus === 'all' || w.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const pendingCount = withdrawals.filter(w => w.status === 'pending').length;
  const processedCount = withdrawals.filter(w => w.status === 'processed').length;

  if (loading) return <div className="loading-spinner">Loading withdrawal requests...</div>;

  return (
    <div className="admin-withdrawals-page">
      <div className="page-header">
        <h1><i className="fas fa-money-bill-wave"></i> Withdrawal Requests</h1>
        <p>Review and process service provider withdrawal requests after manual bank transfer</p>
      </div>

      <div className="stats-row">
        <div className="stat-card">
          <div className="stat-number">{pendingCount}</div>
          <div className="stat-label">Pending Requests</div>
        </div>
        <div className="stat-card">
          <div className="stat-number">{processedCount}</div>
          <div className="stat-label">Processed</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="filter-group">
          <button className={`filter-pill ${filterStatus === 'pending' ? 'active' : ''}`} onClick={() => setFilterStatus('pending')}>Pending</button>
          <button className={`filter-pill ${filterStatus === 'processed' ? 'active' : ''}`} onClick={() => setFilterStatus('processed')}>Processed</button>
          <button className={`filter-pill ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
        </div>
        <div className="search-box">
          <i className="fas fa-search"></i>
          <input
            type="text"
            placeholder="Search by name, email, bank, or account number..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button variant="secondary" onClick={fetchWithdrawals} icon="fa-sync-alt">
          Refresh
        </Button>
      </div>

      {filteredWithdrawals.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-check-circle"></i>
          <h3>No withdrawal requests found</h3>
          <p>All caught up! New requests will appear here when providers request withdrawals.</p>
        </div>
      ) : (
        <div className="withdrawals-list">
          {filteredWithdrawals.map(w => (
            <div key={w._id} className="withdrawal-card">
              <div className="withdrawal-header">
                <div className="provider-info">
                  <h3>{w.vendorId?.name || 'Unknown'}</h3>
                  <p className="email">{w.vendorId?.email || ''}</p>
                </div>
                <div className="amount-badge">${(w.amount || 0).toLocaleString()}</div>
              </div>

              <div className="bank-details">
                <h4><i className="fas fa-university"></i> Bank Account Details</h4>
                <div className="details-grid">
                  <div className="detail-item">
                    <span className="label">Account Holder:</span>
                    <span className="value">{w.accountDetails?.bankAccountName || w.vendorId?.bankAccountName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Account Number:</span>
                    <span className="value">{w.accountDetails?.bankAccountNumber || w.vendorId?.bankAccountNumber || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Bank Name:</span>
                    <span className="value">{w.accountDetails?.bankName || w.vendorId?.bankName || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">IBAN:</span>
                    <span className="value">{w.accountDetails?.bankIban || w.vendorId?.bankIban || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">SWIFT/BIC:</span>
                    <span className="value">{w.accountDetails?.bankSwift || w.vendorId?.bankSwift || 'N/A'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Request Date:</span>
                    <span className="value">{new Date(w.createdAt).toLocaleString()}</span>
                  </div>
                  {w.processedAt && (
                    <div className="detail-item">
                      <span className="label">Processed Date:</span>
                      <span className="value">{new Date(w.processedAt).toLocaleString()}</span>
                    </div>
                  )}
                  {w.adminNotes && (
                    <div className="detail-item full-width">
                      <span className="label">Admin Notes:</span>
                      <span className="value">{w.adminNotes}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="withdrawal-actions">
                {w.status === 'pending' ? (
                  <Button
                    variant="primary"
                    onClick={() => openConfirmModal(w)}
                    disabled={processingId === w._id}
                    icon="fa-check-circle"
                  >
                    {processingId === w._id ? 'Processing...' : 'Mark as Paid'}
                  </Button>
                ) : (
                  <span className="processed-badge">
                    <i className="fas fa-check-circle"></i> Processed
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)} title="Confirm Payment">
        {selectedWithdrawal && (
          <div className="confirm-modal-content">
            <div className="warning-message">
              <i className="fas fa-exclamation-triangle"></i>
              <p>Make sure you have manually transferred <strong>${(selectedWithdrawal.amount || 0).toLocaleString()}</strong> to the provider's bank account before marking as paid.</p>
            </div>
            <div className="bank-summary">
              <p><strong>Provider:</strong> {selectedWithdrawal.vendorId?.name}</p>
              <p><strong>Bank:</strong> {selectedWithdrawal.accountDetails?.bankName || selectedWithdrawal.vendorId?.bankName}</p>
              <p><strong>Account:</strong> {selectedWithdrawal.accountDetails?.bankAccountNumber || selectedWithdrawal.vendorId?.bankAccountNumber}</p>
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={() => handleMarkAsPaid(selectedWithdrawal._id, selectedWithdrawal.vendorId._id, selectedWithdrawal.amount)}
              >
                Confirm & Mark Paid
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default AdminWithdrawals;